import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="score-adjust"
export default class extends Controller {
  static targets = ["display", "input", "saveButton", "incrementButton", "decrementButton"]
  static values = { score: Number, min: Number, max: Number }

  connect() {
    // Set default values if not specified
    this.minValue = this.hasMinValue ? this.minValue : 0
    this.maxValue = this.hasMaxValue ? this.maxValue : 100

    // Initialize score display from data attribute or input value
    if (this.hasScoreValue) {
      this.currentScore = this.scoreValue
    } else if (this.hasInputTarget && this.inputTarget.value) {
      this.currentScore = parseInt(this.inputTarget.value)
    } else {
      this.currentScore = 50 // Default starting score
    }

    this.originalScore = this.currentScore
    this.updateDisplay()
    this.updateButtonStates()
  }

  increment() {
    if (this.currentScore < this.maxValue) {
      this.currentScore++
      this.updateDisplay()
      this.markAsModified()
      this.updateButtonStates()
    }
  }

  decrement() {
    if (this.currentScore > this.minValue) {
      this.currentScore--
      this.updateDisplay()
      this.markAsModified()
      this.updateButtonStates()
    }
  }

  setScore(event) {
    const scoreValue = parseInt(event.params.score)
    if (scoreValue >= this.minValue && scoreValue <= this.maxValue) {
      this.currentScore = scoreValue
      this.updateDisplay()
      this.markAsModified()
      this.updateButtonStates()
    }
  }

  save(event) {
    // Update the hidden input field with current score
    if (this.hasInputTarget) {
      this.inputTarget.value = this.currentScore
    }

    // Add pulse animation to save button
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.add('pulse-green')
      setTimeout(() => {
        this.saveButtonTarget.classList.remove('pulse-green')
      }, 500)
    }

    // Form will submit via Turbo
    // After successful save, the page will update via Turbo Stream
  }

  updateDisplay() {
    if (this.hasDisplayTarget) {
      // Animate counter change
      this.animateCounter(this.displayTarget, this.currentScore)
    }

    // Update hidden input
    if (this.hasInputTarget) {
      this.inputTarget.value = this.currentScore
    }
  }

  animateCounter(element, targetValue) {
    // Simple update - could add smooth counting animation here
    element.textContent = targetValue
  }

  markAsModified() {
    const isModified = this.currentScore !== this.originalScore

    if (this.hasSaveButtonTarget) {
      if (isModified) {
        // Enable and highlight save button
        this.saveButtonTarget.disabled = false
        this.saveButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        this.saveButtonTarget.classList.add('bg-green-600', 'hover:bg-green-700', 'text-white')
      } else {
        // Subtle/disabled state
        this.saveButtonTarget.disabled = false // Keep enabled to allow "confirm without change"
        this.saveButtonTarget.classList.remove('bg-green-600', 'hover:bg-green-700', 'text-white')
        this.saveButtonTarget.classList.add('opacity-50')
      }
    }
  }

  updateButtonStates() {
    // Disable increment at max value
    if (this.hasIncrementButtonTarget) {
      if (this.currentScore >= this.maxValue) {
        this.incrementButtonTarget.disabled = true
        this.incrementButtonTarget.classList.add('opacity-30', 'cursor-not-allowed')
      } else {
        this.incrementButtonTarget.disabled = false
        this.incrementButtonTarget.classList.remove('opacity-30', 'cursor-not-allowed')
      }
    }

    // Disable decrement at min value
    if (this.hasDecrementButtonTarget) {
      if (this.currentScore <= this.minValue) {
        this.decrementButtonTarget.disabled = true
        this.decrementButtonTarget.classList.add('opacity-30', 'cursor-not-allowed')
      } else {
        this.decrementButtonTarget.disabled = false
        this.decrementButtonTarget.classList.remove('opacity-30', 'cursor-not-allowed')
      }
    }
  }
}
